import { defer, Observable, of } from "rxjs"
import { ofType, StateObservable } from "redux-observable"
import { catchError, map, mergeMap, switchMap, takeUntil, tap, timeout } from "rxjs/operators"

import type { Action, StateObject } from "../../../../redux/types"
import type { EpicDependencies } from "../../../../rxjs/configureEpics"
import { getHttpHeaders, getSelectedAccountId } from "../../../../auth/selectors"
import { EventType } from "../../../../../events/types"
import { createEventRequest, requestEvent } from "../../../../../events/utils"
import { logger } from "../../../../../utils/loggerWrap"
import { AnalyticsEventsEnum, sendEvent } from "../../../../../utils/analytics"
import { LOCATION_INITIALIZED, LOCATION_SELECTED } from "../../../../common/locationsPicker/actions"
import { NotificationType, openNotification } from "../../../../uiKit/Notification/Notification"
import {
    GOOGLE_REVIEW_RESPONSE_DELETE_REQUESTED,
    GOOGLE_REVIEW_RESPONSE_SUBMIT_REQUESTED,
    GOOGLE_REVIEWS_FETCH_REQUESTED,
    GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_REQUESTED,
    googleReviewReplySuggestionsFetchFailed,
    googleReviewReplySuggestionsFetchSucceeded,
    googleReviewResponseDeleteFailed,
    googleReviewResponseDeleteSucceeded,
    googleReviewResponseSubmitFailed,
    googleReviewResponseSubmitSucceeded,
    googleReviewsFetchFailed,
    googleReviewsFetchSucceeded,
} from "./actions"
import type { GoogleReviewReplyInput, GoogleReviewReplySuggestionResult } from "./types"
import { fetchGoogleReviewReplySuggestions, fetchGoogleReviews } from "./request"

const GOOGLE_REVIEW_REPLY_TIMEOUT_MS = 20000
const GOOGLE_REVIEW_SUGGESTIONS_TIMEOUT_MS = 20000

const REPLY_FAILED_TITLE = "Could not send response"
const SUGGESTIONS_FAILED_TITLE = "Could not generate responses"
const TRY_AGAIN_LATER = "Please try again later."

export const googleReviewsFetchRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_REVIEWS_FETCH_REQUESTED),
        switchMap((action: Action) => {
            const { locationId } = action.payload
            const headers = getHttpHeaders(deps.getState())

            return defer(() => fetchGoogleReviews(headers, locationId)).pipe(
                map(data => googleReviewsFetchSucceeded(data)),
                catchError(error => {
                    logger.error("Failed to fetch Google Reviews", { error: error, locationId: locationId })
                    return of(googleReviewsFetchFailed(error.message || "Failed to fetch reviews"))
                })
            )
        })
    )

const notifyPartialSuggestionFailure = (results: GoogleReviewReplySuggestionResult[]) => {
    const failedCount = results.filter(result => result.error).length

    if (failedCount === 0) {
        return
    }

    if (failedCount === results.length) {
        openNotification(NotificationType.Error, SUGGESTIONS_FAILED_TITLE, TRY_AGAIN_LATER)
        return
    }

    openNotification(
        NotificationType.Error,
        "Some responses could not be generated",
        `${failedCount} review${failedCount === 1 ? "" : "s"} failed. Please try again later.`
    )
}

export const googleReviewReplySuggestionsFetchRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_REQUESTED),
        // Per-review state means concurrent batches are safe; each row tracks its own request.
        mergeMap((action: Action) => {
            const reviews: GoogleReviewReplyInput[] = action.payload.reviews
            const reviewIds = reviews.map(review => review.reviewId)
            const headers = getHttpHeaders(deps.getState())

            sendEvent(AnalyticsEventsEnum.GoogleReviewSuggestionsGenerated, { batchSize: reviews.length })

            return defer(() => fetchGoogleReviewReplySuggestions(headers, reviews)).pipe(
                timeout(GOOGLE_REVIEW_SUGGESTIONS_TIMEOUT_MS),
                tap(data => notifyPartialSuggestionFailure(data.results)),
                map(data => googleReviewReplySuggestionsFetchSucceeded(data.results)),
                catchError(error => {
                    logger.error("Failed to fetch Google Review suggestions", { error: error, reviewIds: reviewIds })
                    openNotification(NotificationType.Error, SUGGESTIONS_FAILED_TITLE, TRY_AGAIN_LATER)
                    return of(
                        googleReviewReplySuggestionsFetchFailed(
                            reviewIds,
                            error.message || "Failed to fetch suggestions"
                        )
                    )
                }),
                // A different location's suggestions are worthless, and would otherwise
                // leave these reviews stuck in the generating state.
                takeUntil(action$.pipe(ofType(LOCATION_INITIALIZED, LOCATION_SELECTED)))
            )
        })
    )

export const googleReviewResponseSubmitRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_REVIEW_RESPONSE_SUBMIT_REQUESTED),
        mergeMap((action: Action) => {
            const { reviewId, locationId, message } = action.payload
            const headers = getHttpHeaders(deps.getState())
            const accountId = getSelectedAccountId(deps.getState())

            if (!accountId) {
                logger.error("Cannot submit Google Review response: accountId not found in state")
                openNotification(NotificationType.Error, REPLY_FAILED_TITLE, TRY_AGAIN_LATER)
                return of(googleReviewResponseSubmitFailed(reviewId, "Account ID not found"))
            }

            return defer(() =>
                requestEvent(
                    headers,
                    createEventRequest(
                        EventType.GoogleBusinessReviewReplyRequested,
                        {
                            accountId: accountId,
                            locationId: locationId,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            locationId: locationId,
                            reviewId: reviewId,
                            comment: message,
                        }
                    ),
                    GOOGLE_REVIEW_REPLY_TIMEOUT_MS
                )
            ).pipe(
                map(eventData => {
                    const reply = eventData.data?.reply

                    if (!reply) {
                        throw new Error("No reply data returned from event")
                    }

                    logger.info("Google Review response submitted successfully", {
                        reviewId: reviewId,
                        locationId: locationId,
                    })
                    return googleReviewResponseSubmitSucceeded(reviewId, reply)
                }),
                catchError(error => {
                    logger.error("Failed to submit Google Review response", {
                        error: error,
                        reviewId: reviewId,
                        locationId: locationId,
                    })
                    openNotification(NotificationType.Error, REPLY_FAILED_TITLE, TRY_AGAIN_LATER)
                    return of(googleReviewResponseSubmitFailed(reviewId, error.message || "Failed to submit response"))
                })
            )
        })
    )

export const googleReviewResponseDeleteRequestedEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(GOOGLE_REVIEW_RESPONSE_DELETE_REQUESTED),
        mergeMap((action: Action) => {
            const { reviewId, locationId } = action.payload
            const headers = getHttpHeaders(deps.getState())
            const accountId = getSelectedAccountId(deps.getState())

            if (!accountId) {
                logger.error("Cannot delete Google Review response: accountId not found in state")
                openNotification(NotificationType.Error, "Could not delete response", TRY_AGAIN_LATER)
                return of(googleReviewResponseDeleteFailed(reviewId, "Account ID not found"))
            }

            return defer(() =>
                requestEvent(
                    headers,
                    createEventRequest(
                        EventType.GoogleBusinessReviewReplyDeleteRequested,
                        {
                            accountId: accountId,
                            locationId: locationId,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            locationId: locationId,
                            reviewId: reviewId,
                        }
                    ),
                    GOOGLE_REVIEW_REPLY_TIMEOUT_MS
                )
            ).pipe(
                map(() => {
                    logger.info("Google Review response deleted successfully", {
                        reviewId: reviewId,
                        locationId: locationId,
                    })
                    return googleReviewResponseDeleteSucceeded(reviewId)
                }),
                catchError(error => {
                    logger.error("Failed to delete Google Review response", {
                        error: error,
                        reviewId: reviewId,
                        locationId: locationId,
                    })
                    openNotification(NotificationType.Error, "Could not delete response", TRY_AGAIN_LATER)
                    return of(googleReviewResponseDeleteFailed(reviewId, error.message || "Failed to delete response"))
                })
            )
        })
    )

export default [
    googleReviewsFetchRequestedEpic,
    googleReviewReplySuggestionsFetchRequestedEpic,
    googleReviewResponseSubmitRequestedEpic,
    googleReviewResponseDeleteRequestedEpic,
]
